import axios from 'axios';
import { IAlbumParams } from 'redhotpie-shared-lib/interfaces/IGroup';
import { GroupMatchCriteria, PAGING_DEFAULT } from '../constants/constants';
import { addParamsToQueryString } from '../functions/utils';
import { videoUpload } from '../functions/videoUploadHelper';
import { IParams, IParamsRequest } from '../interfaces/IAction';
import { IApiResponse } from '../interfaces/IApiResponse';
import { ClubMediaSortOrder } from '../interfaces/IGroup';
import { ILookUpTagsParams, ISearchGroups } from '../types/clubProfile';
import RestClient from './restClient';
import urls from './urls';
import { generateParams } from './userApi';

export interface IClubMediaSortOrder {
  MediaOrder: {
    MediaID: number;
    SortNumber: number;
  }[];
}

class groupApi {
  static getJoinedGroup(): Promise<IApiResponse> {
    return axios
      .get<any, IApiResponse>(urls.GET_JOINED_GROUP, {
        baseURL: urls.BASE_RHPURL,
        headers: { isAnonymous: false },
      })
      .then(res => res);
  }

  static getRecommendedGroups(params: IParamsRequest): Promise<IApiResponse> {
    const url = `${urls.GET_RECOMMENDED_GROUPS}?Size=${params.size}&Page=${params.page}`;
    return axios
      .get<any, IApiResponse>(url, {
        baseURL: urls.BASE_RHPURL,
        headers: { isAnonymous: false },
      })
      .then(res => res);
  }

  static getMyGroup(): Promise<IApiResponse> {
    return axios
      .get<any, IApiResponse>(urls.GET_MY_GROUP, {
        baseURL: urls.BASE_RHPURL,
        headers: { isAnonymous: false },
      })
      .then(res => res);
  }

  static searchGroups(body: ISearchGroups): Promise<IApiResponse> {
    const url = `${urls.GET_SUGGESTED_GROUP}`;
    return axios
      .post<any, IApiResponse>(url, body, {
        baseURL: urls.BASE_RHPURL,
        headers: { isAnonymous: false },
      })
      .then(res => res);
  }

  static searchPopularGroups(body: ISearchGroups): Promise<IApiResponse> {
    const url = `${urls.SEARCH_GROUP}`;
    return axios
      .post<any, IApiResponse>(url, body, {
        baseURL: urls.BASE_RHPURL,
        headers: { isAnonymous: false },
      })
      .then(res => res);
  }

  static searchGroupByName(name: string): Promise<IApiResponse> {
    const url = `${urls.SEARCH_GROUP_BY_NAME}?Prefix=${name}`;
    return axios
      .get<any, IApiResponse>(url, {
        baseURL: urls.BASE_RHPURL,
        headers: { isAnonymous: false },
      })
      .then(res => res);
  }

  static searchGroupByTagName(name: string): Promise<IApiResponse> {
    let url = `${urls.SEARCH_GROUP_BY_TAG_NAME}`;
    if (name) {
      url += `?Prefix=${name}`;
    }
    return axios
      .get<any, IApiResponse>(url, {
        baseURL: urls.BASE_RHPURL,
        headers: { isAnonymous: false },
      })
      .then(res => res);
  }

  static getGroupDetail(clubId: string, isOwner: boolean): Promise<IApiResponse> {
    let url = urls.GET_GROUP_DETAIL;
    if (isOwner) {
      url = urls.GET_GROUP_DETAIL_OWNER;
    }
    url = url.replace('{ClubId}', clubId);
    return axios
      .get<any, IApiResponse>(url, {
        baseURL: urls.BASE_RHPURL,
        headers: { isAnonymous: false },
      })
      .then(res => res);
  }

  static getClubVoteComments(params: IParamsRequest): Promise<IApiResponse> {
    const url = `${urls.GET_CLUB_VOTE_COMMENTS.replace('{ID}', `${params.id || '-1'}`)}?Size=${params.size}&Page=${params.page}`;
    return axios.get(url, {
      baseURL: urls.BASE_RHPURL,
      headers: { isAnonymous: false },
    });
  }

  static getAllGroupReviews(params: IParamsRequest): Promise<IApiResponse> {
    const url = `${urls.GET_ALL_CLUB_REVIEWS}?Size=${params.size}&Page=${params.page}`;
    return axios.get(url, {
      baseURL: urls.BASE_RHPURL,
      headers: { isAnonymous: false },
    });
  }

  static getGroupJoinedUsers(clubID: string, params: any): Promise<IApiResponse> {
    const baseUrl = urls.GET_GROUP_JOINED_USERS.replace('{ClubId}', clubID);
    const url = addParamsToQueryString(baseUrl, params);
    return axios
      .get<any, IApiResponse>(url, {
        baseURL: urls.BASE_RHPURL,
        headers: { isAnonymous: false },
      })
      .then(res => res);
  }

  static postGroupComment(params: any): Promise<IApiResponse> {
    const url = urls.COMMENT_GROUP;
    return axios
      .post<any, IApiResponse>(url, params, {
        baseURL: urls.BASE_RHPURL,
        headers: { isAnonymous: false },
      })
      .then(res => res);
  }

  static postGroupVote(clubID: string, params: any): Promise<IApiResponse> {
    const url = urls.VOTE_GROUP.replace('{ClubId}', clubID);
    return axios
      .post<any, IApiResponse>(url, params, {
        baseURL: urls.BASE_RHPURL,
        headers: { isAnonymous: false },
      })
      .then(res => res);
  }

  static joinAGroup(clubID: string): Promise<IApiResponse> {
    const url = urls.JOIN_GROUP.replace('{ClubId}', clubID);
    return axios
      .post<any, IApiResponse>(
        url,
        { id: clubID },
        {
          baseURL: urls.BASE_RHPURL,
          headers: { isAnonymous: false },
        }
      )
      .then(res => res);
  }

  static acceptInvitationJoinGroup(clubID: string): Promise<IApiResponse> {
    const url = urls.ACCEPT_INVITATION_GROUP.replace('{ClubId}', clubID);
    return axios
      .post<any, IApiResponse>(
        url,
        { id: clubID },
        {
          baseURL: urls.BASE_RHPURL,
          headers: { isAnonymous: false },
        }
      )
      .then(res => res);
  }

  static rejectInvitationJoinGroup(clubID: string): Promise<IApiResponse> {
    const url = urls.REJECT_INVITATION_GROUP.replace('{ClubId}', clubID);
    return axios
      .post<any, IApiResponse>(
        url,
        { id: clubID },
        {
          baseURL: urls.BASE_RHPURL,
          headers: { isAnonymous: false },
        }
      )
      .then(res => res);
  }

  static leaveAGroup(clubID: string): Promise<IApiResponse> {
    const url = urls.LEAVE_GROUP.replace('{ClubId}', clubID);
    return axios
      .post<any, IApiResponse>(
        url,
        { id: clubID },
        {
          baseURL: urls.BASE_RHPURL,
          headers: { isAnonymous: false },
        }
      )
      .then(res => res);
  }

  static getListUserRequestJoinClub(
    clubID: string,
    params: { searchUserName?: string; matching?: number; page?: number; size?: number }
  ): Promise<IApiResponse> {
    const baseUrl = urls.GET_LIST_REQUESTED_USERS.replace('{ClubId}', clubID);
    const _params: any = {
      page: params.page || 1,
      size: params.size || 25,
      matching: params.matching ?? GroupMatchCriteria.All,
    };
    if (params.searchUserName && params.searchUserName.length > 0) {
      _params.searchUserName = params.searchUserName;
    }
    const url = addParamsToQueryString(baseUrl, _params);
    return axios
      .get<any, IApiResponse>(url, {
        baseURL: urls.BASE_RHPURL,
        headers: { isAnonymous: false },
      })
      .then(res => res);
  }

  static patchApproveUserJoinGroupRequest(clubID: string, params: {}): Promise<IApiResponse> {
    const url = urls.APPROVE_USER_REQUEST.replace('{ClubId}', clubID);
    return axios
      .patch<any, IApiResponse>(url, params, {
        baseURL: urls.BASE_RHPURL,
        headers: { isAnonymous: false },
      })
      .then(res => res);
  }

  static patchRemoveUserJoinGroupRequest(clubID: string, params: {}): Promise<IApiResponse> {
    const url = urls.REMOVE_USER_REQUEST.replace('{ClubId}', clubID);
    return axios
      .patch<any, IApiResponse>(url, params, {
        baseURL: urls.BASE_RHPURL,
        headers: { isAnonymous: false },
      })
      .then(res => res);
  }

  static patchClubUserMemberLevel(clubID: string, userID: string, params: {}): Promise<IApiResponse> {
    const url = urls.PATCH_CLUB_USER_MEMBER_LEVEL.replace('{ClubID}', clubID).replace('{UserID}', userID);
    return axios
      .patch<any, IApiResponse>(url, params, {
        baseURL: urls.BASE_RHPURL,
        headers: { isAnonymous: false },
      })
      .then(res => res);
  }

  static patchClubUserBanPosting(
    clubID: string,
    userID: string,
    params: {
      isBanned: boolean;
    }
  ): Promise<IApiResponse> {
    const url = urls.PATCH_CLUB_USER_BAN_POSTING.replace('{ClubID}', clubID).replace('{UserID}', userID);
    const _params: any = {
      IsBanned: params.isBanned,
    };
    return axios
      .patch<any, IApiResponse>(url, _params, {
        baseURL: urls.BASE_RHPURL,
        headers: { isAnonymous: false },
      })
      .then(res => res);
  }

  static searchGroupByTagId(tagId: number, params: IParamsRequest): Promise<IApiResponse> {
    const queryString = generateParams({ ...params });
    const url = `${urls.SEARCH_GROUP_BY_TAG_ID.replace('{Id}', tagId.toString())}${queryString ? '?' : ''}${queryString}`;
    return axios
      .get<any, IApiResponse>(url, {
        baseURL: urls.BASE_RHPURL,
        headers: { isAnonymous: false },
      })
      .then(res => res);
  }

  static joinAPrivateGroup(clubID: string): Promise<IApiResponse> {
    const url = urls.JOIN_PRIVATE_GROUP.replace('{ClubId}', clubID);
    return axios
      .post<any, IApiResponse>(
        url,
        { id: clubID },
        {
          baseURL: urls.BASE_RHPURL,
          headers: { isAnonymous: false },
        }
      )
      .then(res => res);
  }

  static leaveAPrivateGroup(clubID: string): Promise<IApiResponse> {
    const url = urls.LEAVE_PRIVATE_GROUP.replace('{ClubId}', clubID);
    return axios
      .post<any, IApiResponse>(
        url,
        { id: clubID },
        {
          baseURL: urls.BASE_RHPURL,
          headers: { isAnonymous: false },
        }
      )
      .then(res => res);
  }

  static createGroup(params: any): Promise<IApiResponse> {
    const url = `${urls.CREATE_GROUP}`;
    return axios
      .post<any, IApiResponse>(url, params, {
        baseURL: urls.BASE_RHPURL,
        headers: { isAnonymous: false },
      })
      .then(res => res);
  }

  static updateGroup(clubId: string, params: any): Promise<IApiResponse> {
    const url = `${urls.USER_GROUP_DETAIL}`.replace('{ClubId}', clubId);
    return axios
      .patch<any, IApiResponse>(url, params, {
        baseURL: urls.BASE_RHPURL,
        headers: { isAnonymous: false },
      })
      .then(res => res);
  }

  static removeGroup(clubId: string): Promise<IApiResponse> {
    const url = `${urls.USER_GROUP_DETAIL}`.replace('{ClubId}', clubId);
    return axios
      .delete<any, IApiResponse>(url, {
        baseURL: urls.BASE_RHPURL,
        headers: { isAnonymous: false },
      })
      .then(res => res);
  }

  static getGroupActivity(params: { clubId: string; isOwner: boolean; page?: number; size?: number }): any {
    let url = urls.GET_GROUP_ACTIVITY;
    let _params = {
      page: params.page || 1,
      size: params.size || 25,
    };
    if (params.isOwner) {
      url = urls.GET_GROUP_ACTIVITY_OWNER;
    }
    url = addParamsToQueryString(`${url.replace('{ClubId}', params.clubId)}`, _params);
    return axios
      .get(url, {
        baseURL: urls.BASE_RHPURL,
        headers: { isAnonymous: false },
      })
      .then(res => res);
  }

  static getGroupAlbums(params: { clubId: string; isOwner: boolean; page?: number; size?: number }): any {
    let url = urls.GET_GROUP_ALBUMS;
    let _params = {
      page: params.page || 1,
      size: params.size || 25,
    };
    if (params.isOwner) {
      url = urls.GET_GROUP_ALBUMS_OWNER;
    }
    url = addParamsToQueryString(`${url.replace('{ClubId}', params.clubId)}`, _params);
    return axios
      .get(url, {
        baseURL: urls.BASE_RHPURL,
        headers: { isAnonymous: false },
      })
      .then(res => res);
  }

  static getGroupPhotos(params: { clubId: string; isOwner: boolean; page?: number; size?: number }): Promise<IApiResponse> {
    let url = urls.GET_GROUP_PHOTOS;
    let _params = {
      page: params.page || 1,
      size: params.size || 25,
    };
    if (params.isOwner) {
      url = urls.GET_GROUP_PHOTOS_OWNER;
    }
    url = addParamsToQueryString(`${url.replace('{ClubId}', params.clubId)}`, _params);
    return axios
      .get<any, IApiResponse>(url, {
        baseURL: urls.BASE_RHPURL,
        headers: { isAnonymous: false },
      })
      .then(res => res);
  }

  static getGroupVideos(params: { clubId: string; isOwner: boolean; page?: number; size?: number }): Promise<IApiResponse> {
    let url = urls.GET_GROUP_VIDEO;
    let _params = {
      page: params.page || 1,
      size: params.size || 25,
    };
    if (params.isOwner) {
      url = urls.GET_GROUP_VIDEO_OWNER;
    }
    url = addParamsToQueryString(`${url.replace('{ClubId}', params.clubId)}`, _params);
    return axios
      .get<any, IApiResponse>(url, {
        baseURL: urls.BASE_RHPURL,
        headers: { isAnonymous: false },
      })
      .then(res => res);
  }

  static getGroupEvents(params: {
    clubId: string;
    isOwner: boolean;
    isApprovedOnly?: boolean;
    page?: number;
    size?: number;
    type?: string;
  }): Promise<IApiResponse> {
    let url = urls.GET_GROUP_EVENTS;
    let _params = {
      page: params.page || 1,
      size: params.size || 25,
      type: params.type,
      isApprovedOnly: params.isApprovedOnly || false,
    };
    if (params.isOwner) {
      url = urls.GET_GROUP_EVENTS_OWNER;
    }
    url = addParamsToQueryString(`${url.replace('{ClubId}', params.clubId)}`, _params);
    return axios.get(url, {
      baseURL: urls.BASE_RHPURL,
      headers: { isAnonymous: false },
    });
  }

  static lookupGroupTags(params: ILookUpTagsParams): Promise<IApiResponse<any>> {
    const _params = {
      prefix: params.prefix,
      size: params.size || 25,
      page: params.page || 1,
    };
    const queryString = generateParams({ ..._params });
    const url = `${urls.LOOK_UP_GROUP_TAGS}?${queryString}`;
    return axios
      .get(url, {
        baseURL: urls.BASE_RHPURL,
        headers: { isAnonymous: false },
      })
      .then(res => res.data);
  }

  static getClubMediaComment = (params: IParams): Promise<IApiResponse> => {
    const _params = {
      size: params.Size || 25,
      page: params.Page || 1,
    };
    const url = addParamsToQueryString(urls.CLUB_MEDIA_COMMENT.replace('{Id}', params.ID.toString()), _params);
    return axios.get(url, {
      baseURL: urls.BASE_RHPURL,
      headers: { isAnonymous: false },
    });
  };
  static postClubMediaComment = (params: IParams): Promise<IApiResponse> => {
    const url = urls.CLUB_MEDIA_COMMENT.replace('{Id}', params.ID);
    let isFormData: boolean = false;
    if (params.body.MessageContentType !== 'Text' && params.body.MessageContentType !== 'Giphy') {
      isFormData = true;
    }
    const restClient = new RestClient();
    restClient.setBaseUrl(urls.BASE_RHPURL);
    // @ts-ignore
    return restClient.execute(url, 'POST', params.body, false, false, false, isFormData).then(res => res);
    // return axios.post(url, params.body, {baseURL: urls.BASE_RHPURL, headers});
  };
  static getMediaLike = (params: IParams): Promise<IApiResponse> => {
    const _params = {
      size: params.size || 25,
      page: params.page || 1,
    };
    const url = addParamsToQueryString(urls.CLUB_MEDIA_LIKE.replace('{Id}', params.id.toString()), _params);
    return axios.get(url, {
      baseURL: urls.BASE_RHPURL,
      headers: { isAnonymous: false },
    });
  };
  static postMediaLike = (id: number | string): Promise<IApiResponse> => {
    const url = urls.CLUB_MEDIA_LIKE.replace('{Id}', id.toString());
    return axios.post(url, null, {
      baseURL: urls.BASE_RHPURL,
      headers: { isAnonymous: false },
    });
  };

  static deleteMediaLike(id: number | string): Promise<IApiResponse> {
    const url = urls.CLUB_MEDIA_LIKE.replace('{Id}', id.toString());
    return axios.patch(url, null, {
      baseURL: urls.BASE_RHPURL,
      headers: { isAnonymous: false },
    });
  }

  static getClubAlbumComment = (params: IParams): Promise<IApiResponse> => {
    const _params = {
      size: params.Size || 25,
      page: params.Page || 1,
    };
    const url = addParamsToQueryString(urls.CLUB_ALBUM_COMMENT.replace('{Id}', params.ID.toString()), _params);
    return axios.get(url, {
      baseURL: urls.BASE_RHPURL,
      headers: { isAnonymous: false },
    });
  };
  static postClubAlbumComment = (params: IParams): Promise<IApiResponse> => {
    const url = urls.CLUB_ALBUM_COMMENT.replace('{Id}', params.ID);
    let isFormData: boolean = false;
    if (params.body.MessageContentType !== 'Text' && params.body.MessageContentType !== 'Giphy') {
      isFormData = true;
    }
    const restClient = new RestClient();
    restClient.setBaseUrl(urls.BASE_RHPURL);
    // @ts-ignore
    return restClient.execute(url, 'POST', params.body, false, false, false, isFormData).then(res => res);
  };
  static getAlbumLike = (params: IParams): Promise<IApiResponse> => {
    const _params = {
      size: params.size || 25,
      page: params.page || 1,
    };
    const url = addParamsToQueryString(urls.CLUB_ALBUM_LIKE.replace('{Id}', params.id.toString()), _params);
    return axios.get(url, {
      baseURL: urls.BASE_RHPURL,
      headers: { isAnonymous: false },
    });
  };
  static postAlbumLike = (id: number | string): Promise<IApiResponse> => {
    const url = urls.CLUB_ALBUM_LIKE.replace('{Id}', id.toString());
    return axios.post(url, null, {
      baseURL: urls.BASE_RHPURL,
      headers: { isAnonymous: false },
    });
  };
  static postClubBanner = (clubID: string, body: any): Promise<IApiResponse> => {
    const url = urls.POST_CLUB_BANNER.replace('{ClubID}', clubID);
    const restClient = new RestClient();
    restClient.setBaseUrl(urls.BASE_RHPURL);
    // restClient.setHeaders({contentType: 'multipart/form-data'})
    // @ts-ignore
    return restClient.execute(url, 'POST', body, false, false, false, true).then(res => res);
    // return axios.post(url, body, { baseURL: urls.BASE_RHPURL, headers: { isAnonymous: false } });
  };

  static deleteAlbumLike(id: number | string): Promise<IApiResponse> {
    const url = urls.CLUB_ALBUM_LIKE.replace('{Id}', id.toString());
    return axios.patch(url, null, {
      baseURL: urls.BASE_RHPURL,
      headers: { isAnonymous: false },
    });
  }

  static getUserClubFriend(clubID: string, params: any) {
    const baseUrl = `${urls.GET_CLUB_FRIENDS.replace('{ClubID}', clubID)}`;
    const url = addParamsToQueryString(baseUrl, params);
    return axios
      .get(url, {
        baseURL: urls.BASE_RHPURL,
        headers: { isAnonymous: false },
      })
      .then(res => res.data);
  }

  static getGroupTagPopular(): Promise<IApiResponse> {
    const baseUrl = `${urls.GET_CLUB_TAG_PUPOLAR}`;
    const url = addParamsToQueryString(baseUrl);
    return axios.get(url, {
      baseURL: urls.BASE_RHPURL,
      headers: { isAnonymous: false },
    });
  }
  static getUserClubAlertByClubID(clubID: string) {
    const params = {};
    const queryString = '';
    const url = `${urls.GET_USER_CLUB_ALERT_BY_CLUB_ID.replace('{ClubID}', clubID)}`;
    return axios
      .get(url, {
        baseURL: urls.BASE_RHPURL,
        headers: { isAnonymous: false },
      })
      .then(res => res.data);
  }
  static getGroupUserActivites(clubID: string, params?: { page?: number; pageSize?: number }) {
    const baseUrl = `${urls.GET_CLUB_USER_ACTIVITIES.replace('{ClubID}', clubID)}`;
    const url = addParamsToQueryString(baseUrl, params);
    return axios
      .get(url, {
        baseURL: urls.BASE_RHPURL,
        headers: { isAnonymous: false },
      })
      .then(res => res);
  }

  static getGroupEventExpressInterest(clubID: string, params?: { page?: number; pageSize?: number }) {
    const baseUrl = `${urls.GET_CLUB_EVENT_EXPRESS_INTEREST.replace('{ClubID}', clubID)}`;
    const url = addParamsToQueryString(baseUrl, params);
    return axios
      .get(url, {
        baseURL: urls.BASE_RHPURL,
        headers: { isAnonymous: false },
      })
      .then(res => res);
  }

  static getGroupAlbumMedia(params: { albumId: string; isOwner: boolean; page?: number; size?: number }): any {
    let url = urls.GET_CLUB_ALBUM_MEDIA;
    let _params = {
      page: params.page || 1,
      size: params.size || 50,
    };
    const queryString = generateParams({ ..._params });
    if (params.isOwner) {
      url = urls.GET_CLUB_ALBUM_MEDIA_OWNER;
    }
    url = `${url.replace('{AlbumID}', params.albumId)}?${queryString}`;
    return axios
      .get(url, {
        baseURL: urls.BASE_RHPURL,
        headers: { isAnonymous: false },
      })
      .then(res => res);
  }

  static getClubPostComment = (params: IParams): Promise<IApiResponse> => {
    const _params = {
      size: params.Size || 25,
      page: params.Page || 1,
    };
    const url = addParamsToQueryString(urls.CLUB_POST_COMMENT.replace('{Id}', params.ID.toString()), _params);
    return axios.get(url, {
      baseURL: urls.BASE_RHPURL,
      headers: { isAnonymous: false },
    });
  };
  static postClubPostComment = (params: IParams): Promise<IApiResponse> => {
    const url = urls.CLUB_POST_COMMENT.replace('{Id}', params.ID);
    let isFormData: boolean = false;
    if (params.body.MessageContentType !== 'Text' && params.body.MessageContentType !== 'Giphy') {
      isFormData = true;
    }
    const restClient = new RestClient();
    restClient.setBaseUrl(urls.BASE_RHPURL);
    // @ts-ignore
    return restClient.execute(url, 'POST', params.body, false, false, false, isFormData).then(res => res);
  };
  static getClubPostLikes = (params: IParams): Promise<IApiResponse> => {
    const _params = {
      size: params.Size || 25,
      page: params.Page || 1,
    };
    const url = addParamsToQueryString(urls.GET_CLUB_POST_LIKES.replace('{Id}', params.ID.toString()), _params);
    return axios.get(url, {
      baseURL: urls.BASE_RHPURL,
      headers: { isAnonymous: false },
    });
  };
  static postClubPostLike = (id: number | string): Promise<IApiResponse> => {
    const url = urls.CLUB_POST_LIKE.replace('{Id}', id.toString());
    return axios.post(url, null, {
      baseURL: urls.BASE_RHPURL,
      headers: { isAnonymous: false },
    });
  };

  static deleteClubPostLike(id: number | string): Promise<IApiResponse> {
    const url = urls.CLUB_POST_LIKE.replace('{Id}', id.toString());
    return axios.patch(url, null, {
      baseURL: urls.BASE_RHPURL,
      headers: { isAnonymous: false },
    });
  }

  static getClubPostDetail({ id, isOwner }: { id: number | string; isOwner: boolean }): Promise<IApiResponse> {
    let url = urls.GET_CLUB_POST_DETAIL;
    if (isOwner) {
      url = urls.GET_CLUB_POST_DETAIL_OWNER;
    }
    url = url.replace('{PostID}', id.toString());
    return axios.get(url, {
      baseURL: urls.BASE_RHPURL,
      headers: { isAnonymous: false },
    });
  }

  static getClubListCanBeInvited(userId: number | string): Promise<IApiResponse> {
    const url = addParamsToQueryString(urls.GET_CLUB_LIST_CAN_SHARE, {
      UserID: userId,
    });
    return axios.get(url, {
      baseURL: urls.BASE_RHPURL,
      headers: { isAnonymous: false },
    });
  }

  static postInviteUserToJoinGroup(clubId: number | string, userId: number | string): Promise<IApiResponse> {
    const url = addParamsToQueryString(urls.POST_INVITE_USER_TO_JOIN.replace('{ClubID}', clubId.toString()));
    return axios.post(
      url,
      { UserIDs: [userId] },
      {
        baseURL: urls.BASE_RHPURL,
        headers: { isAnonymous: false },
      }
    );
  }

  static postAcceptInvitationJoinGroup(clubId: number | string): Promise<IApiResponse> {
    const url = addParamsToQueryString(urls.POST_ACCECPT_INVITATION.replace('{ClubID}', clubId.toString()));
    return axios.post(url, null, {
      baseURL: urls.BASE_RHPURL,
      headers: { isAnonymous: false },
    });
  }

  static postRejectInvitationJoinGroup(clubId: number | string): Promise<IApiResponse> {
    const url = addParamsToQueryString(urls.POST_REJECT_INVITATION.replace('{ClubID}', clubId.toString()));
    return axios.post(url, null, {
      baseURL: urls.BASE_RHPURL,
      headers: { isAnonymous: false },
    });
  }

  static createClubPost(params: { clubId: string | number; message: string; eventId?: number }): Promise<IApiResponse> {
    const url = urls.CREATE_USER_CLUB_POST.replace('{ClubID}', params.clubId.toString());
    return axios.post(
      url,
      { Message: params.message, EventID: params.eventId },
      { baseURL: urls.BASE_RHPURL, headers: { isAnonymous: false } }
    );
  }

  static editClubPost(params: { postId: string | number; message: string; eventId?: string | number }): Promise<IApiResponse> {
    const url = urls.USER_CLUB_POST.replace('{PostID}', params.postId.toString());

    return axios.patch(
      url,
      { Message: params.message, EventID: params.eventId },
      { baseURL: urls.BASE_RHPURL, headers: { isAnonymous: false } }
    );
  }

  static deleteClubPost(id: number | string): Promise<IApiResponse> {
    const url = urls.USER_CLUB_POST.replace('{PostID}', id.toString());
    return axios.delete(url, {
      baseURL: urls.BASE_RHPURL,
      headers: { isAnonymous: false },
    });
  }
  static createClubAlbum(params: { clubId: string | number; title: string }): Promise<IApiResponse> {
    const url = urls.CREATE_USER_CLUB_ALBUM.replace('{ClubID}', params.clubId.toString());
    return axios.post(url, { Title: params.title }, { baseURL: urls.BASE_RHPURL, headers: { isAnonymous: false } });
  }

  static editClubAlbum(params: { albumId: string | number; title: string }): Promise<IApiResponse> {
    const url = urls.USER_CLUB_ALBUM.replace('{AlbumID}', params.albumId.toString());
    return axios.patch(url, { Title: params.title }, { baseURL: urls.BASE_RHPURL, headers: { isAnonymous: false } });
  }
  static deleteClubAlbum(id: number | string): Promise<IApiResponse> {
    const url = urls.USER_CLUB_ALBUM.replace('{AlbumID}', id.toString());
    return axios.delete(url, {
      baseURL: urls.BASE_RHPURL,
      headers: { isAnonymous: false },
    });
  }

  static createClubMediaPhoto(data: any): Promise<IApiResponse> {
    const url = urls.POST_CREATE_MEDIA_PHOTO;
    const restClient = new RestClient();
    restClient.setBaseUrl(urls.BASE_RHPURL);
    // @ts-ignore
    return restClient.execute(url, 'POST', data, false, false, false, true).then(res => res);
    // return axios.post(url, data, {
    //     baseURL: urls.BASE_RHPURL,
    //     headers: {isAnonymous: false, 'Content-Type': 'multipart/form-data'}
    // });
  }
  static createClubMediaVideo(data: any): Promise<IApiResponse> {
    const url = urls.MERGE_CLUB_VIDEO_ASYNC;
    const urlProgress = urls.USER_CLUB_VIDEO_UPLOAD_PROGRESS;
    const params = {
      ...data,
      mergeURL: url,
      progressUrl: urlProgress,
      id: data.id,
      compress: data.compress,
      mergedMetaData: {
        ParentID: data.parentID,
        ParentType: data.parentType,
        SortNumber: data.sortNumber,
      },
      file: data.file,
    };
    // @ts-ignore
    return videoUpload(params);
  }
  static deleteClubMedia(mediaId: any) {
    const url = urls.DELETE_CLUB_MEDIA.replace('{MediaID}', mediaId);
    return axios.delete(url, {
      baseURL: urls.BASE_RHPURL,
      headers: { isAnonymous: false },
    });
  }

  static deleteClubPostMedias(postId: number, mediaIds: number[]) {
    const mediaIdQueryParams = mediaIds.map(id => `MediaID=${id}`).join('&');
    let url = urls.DELETE_CLUB_POST_MEDIAS.replace('{PostID}', postId.toString());
    if (mediaIdQueryParams) {
      url = `${url}?${mediaIdQueryParams}`;
    }
    return axios.delete<any, IApiResponse>(url, {
      baseURL: urls.BASE_RHPURL,
      headers: { isAnonymous: false },
    });
  }

  static deleteClubAlbumMedias(albumId: number, mediaIds: number[]) {
    const mediaIdQueryParams = mediaIds.map(id => `MediaID=${id}`).join('&');
    let url = urls.DELETE_CLUB_ALBUM_MEDIAS.replace('{AlbumID}', albumId.toString());
    if (mediaIdQueryParams) {
      url = `${url}?${mediaIdQueryParams}`;
    }
    return axios.delete<any, IApiResponse>(url, {
      baseURL: urls.BASE_RHPURL,
      headers: { isAnonymous: false },
    });
  }

  static reOrderUserClubAlbumMedia(albumId: number, mediaOrder: ClubMediaSortOrder[]) {
    const body: IClubMediaSortOrder = { MediaOrder: mediaOrder.map(item => ({ MediaID: item.mediaId, SortNumber: item.sortNumber })) };
    let url = urls.RE_ORDER_USER_CLUB_ALBUM_MEDIA.replace('{AlbumID}', albumId.toString());
    return axios.patch<any, IApiResponse>(url, body, {
      baseURL: urls.BASE_RHPURL,
      headers: { isAnonymous: false },
    });
  }
  static reOrderUserClubPostMedia(postId: number, mediaOrder: ClubMediaSortOrder[]) {
    const body: IClubMediaSortOrder = { MediaOrder: mediaOrder.map(item => ({ MediaID: item.mediaId, SortNumber: item.sortNumber })) };
    let url = urls.RE_ORDER_USER_CLUB_POST_MEDIA.replace('{PostID}', postId.toString());
    return axios.patch<any, IApiResponse>(url, body, {
      baseURL: urls.BASE_RHPURL,
      headers: { isAnonymous: false },
    });
  }

  static getAlbumDetail(params: IAlbumParams): Promise<IApiResponse> {
    let url = urls.GET_CLUB_ALBUM_DETAIL;
    if (params.isOwner) {
      url = urls.USER_CLUB_ALBUM;
    }
    url = url.replace('{AlbumID}', params.albumId);

    return axios.get(url, {
      baseURL: urls.BASE_RHPURL,
      headers: { isAnonymous: false },
    });
  }

  static getGroupPostMedia(params: { postId: string; page?: number; size?: number; isOwner?: boolean }): any {
    let url = urls.GET_CLUB_POST_MEDIA;
    if (params.isOwner) {
      url = urls.GET_CLUB_POST_MEDIA_OWNER;
    }
    let _params = {
      page: params.page || 1,
      size: params.size || 25,
    };
    const queryString = generateParams({ ..._params });
    url = `${url.replace('{PostID}', params.postId)}?${queryString}`;
    return axios
      .get(url, {
        baseURL: urls.BASE_RHPURL,
        headers: { isAnonymous: false },
      })
      .then(res => res);
  }

  static getUserClubJoinedUsers(clubId: string, params: { page?: number; size?: number; SearchUserName?: string }): any {
    let url = `${urls.GET_LIST_JOINED_USERS.replace('{ClubId}', clubId)}?Size=${params.size}&Page=${params.page}`;
    if (params.SearchUserName) {
      url += `&SearchUserName=${params.SearchUserName}`;
    }
    return axios.get(url, {
      baseURL: urls.BASE_RHPURL,
      headers: { isAnonymous: false },
    });
  }

  static getClubMedia(mediaId: string, isOwner: boolean): Promise<IApiResponse> {
    const url = `${isOwner ? urls.GET_USER_CLUB_MEDIA : urls.GET_CLUB_MEDIA}`.replace('{Id}', mediaId);
    return axios.get(url, {
      baseURL: urls.BASE_RHPURL,
      headers: { isAnonymous: false },
    });
  }
  static deleteClubValidation(commentId: number, isOwner?: boolean): Promise<IApiResponse> {
    const url = isOwner
      ? urls.DELETE_PARENT_OWNED_COMMENT.replace('{Id}', commentId.toString())
      : urls.DELETE_COMMENT.replace('{ID}', commentId.toString());
    return axios.delete(url, {
      baseURL: urls.BASE_RHPURL,
      headers: { isAnonymous: false },
    });
  }
  static deleteClubVote(voteId: number, clubId: string | number): Promise<IApiResponse> {
    const url = urls.DELETE_CLUB_VOTE.replace('{VoteID}', `${voteId}`);
    return axios.delete(url, {
      baseURL: urls.BASE_RHPURL,
      headers: { isAnonymous: false },
    });
  }
  static deleteClubVoteOwner(voteId: number): Promise<IApiResponse> {
    const url = urls.DELETE_CLUB_VOTE_OWNER.replace('{VoteID}', voteId.toString());
    return axios.delete(url, {
      baseURL: urls.BASE_RHPURL,
      headers: { isAnonymous: false },
    });
  }
  static postClubVote(params: IParamsRequest): Promise<IApiResponse> {
    const url = urls.POST_CREATE_UPDATE_CLUB_VOTE.replace('{ID}', `${params.ID || '-1'}`);
    return axios.post(url, params, { baseURL: urls.BASE_RHPURL, headers: { isAnonymous: false } });
  }
  static getUserClubAlert(params: { pageSize?: number; page?: number }) {
    const url = `${urls.GET_USER_CLUB_ALERT}`;
    const queryString = generateParams({
      Page: params?.page || 1,
      Size: params?.pageSize || PAGING_DEFAULT.PAGE_SIZE_MEMBER_SEARCH,
      // DemoMode: true,
    });
    return axios
      .get(`${url}${queryString ? '?' : ''}${queryString}`, {
        baseURL: urls.BASE_RHPURL,
        headers: { isAnonymous: false },
      })
      .then(res => res.data);
  }
  static getGroupActivitiesNotification(params: { pageSize?: number; page?: number }) {
    const queryString = generateParams({
      Page: params?.page || 1,
      Size: params?.pageSize || PAGING_DEFAULT.PAGE_SIZE_MEMBER_SEARCH,
      // DemoMode: true,
    });
    const url = `${urls.GET_GROUP_ACTIVITIES_NOTIFICATION}${queryString ? '?' : ''}${queryString}`;
    // return this.restClient.execute(url, ApiMethod.GET, null).then(res => res);
    return axios.get(url, { baseURL: urls.BASE_RHPURL, headers: { isAnonymous: false } });
  }
  static getGroupJoinRequestNotification(params: { pageSize?: number; page?: number }) {
    const queryString = generateParams({
      Page: params?.page || 1,
      Size: params?.pageSize || PAGING_DEFAULT.PAGE_SIZE_MEMBER_SEARCH,
      // DemoMode: true,
    });
    const url = `${urls.GET_GROUP_JOIN_REQUEST_NOTIFICATION}${queryString ? '?' : ''}${queryString}`;
    // return this.restClient.execute(url, ApiMethod.GET, null).then(res => res);
    return axios.get(url, { baseURL: urls.BASE_RHPURL, headers: { isAnonymous: false } });
  }

  static getSuggestedUsers(params: {
    clubId: string;
    pageSize?: number;
    page?: number;
    memberType?: number;
    ageMax?: number;
    ageMin?: number;
  }): Promise<IApiResponse> {
    let paramsQuery = {
      Page: params?.page || 1,
      Size: params?.pageSize || PAGING_DEFAULT.PAGE_SIZE_MEMBER_SEARCH,
      MemberType: -1,
      AgeMax: -1,
      AgeMin: -1,
    };
    if (params.memberType) {
      paramsQuery['MemberType'] = params.memberType;
    }
    if (paramsQuery['MemberType'] === -1) {
      delete (paramsQuery as { MemberType?: number }).MemberType;
    }
    if (params.ageMax) {
      paramsQuery['AgeMax'] = params.ageMax;
    } else {
      delete (paramsQuery as { AgeMax?: number }).AgeMax;
    }
    if (params.ageMin) {
      paramsQuery['AgeMin'] = params.ageMin;
    } else {
      delete (paramsQuery as { AgeMin?: number }).AgeMin;
    }
    const queryString = generateParams(paramsQuery);
    const url = `${urls.GET_GROUP_SUGGESTED_MEMBERS.replace('{ClubID}', params.clubId)}${queryString ? '?' : ''}${queryString}`;
    return axios.get(url, { baseURL: urls.BASE_RHPURL, headers: { isAnonymous: false } });
  }

  static getGroupChatCount(params: { clubId: string }) {
    const url = urls.GET_GROUP_CHAT_COUNT.replace('{groupId}', params.clubId);
    return axios.get(url, { baseURL: urls.BASE_URL, headers: { isAnonymous: false } });
  }

  static getGroupChats(params: { clubId: string }): Promise<IApiResponse> {
    const url = urls.GET_GROUP_CHATS.replace('{groupId}', params.clubId);
    return axios.get(url, { baseURL: urls.BASE_URL, headers: { isAnonymous: false } });
  }

  static getGroupReceivedInvites(params: IParamsRequest): Promise<IApiResponse> {
    const url = `${urls.GET_GROUP_RECEIVED_INVITES}?Size=${params.size}&Page=${params.page}`;
    return axios.get(url, { baseURL: urls.BASE_RHPURL, headers: { isAnonymous: false } });
  }
  static getGroupRecentActivity(params: { page?: number; size?: number }): Promise<IApiResponse> {
    const queryString = generateParams({
      Page: params?.page || 1,
      Size: params?.size || PAGING_DEFAULT.PAGE_SIZE_MEMBER_SEARCH,
    });
    const url = `${urls.GET_GROUP_RECENT_ACTIVITY}${queryString ? '?' : ''}${queryString}`;
    return axios.get(url, { baseURL: urls.BASE_RHPURL, headers: { isAnonymous: false } });
  }

  static deleteGroupReceivedInvite(inviteId: number): Promise<IApiResponse> {
    const url = `${urls.DELETE_GROUP_RECEIVED_INVITES}`.replace('{InviteID}', inviteId.toString());
    return axios.delete(url, {
      baseURL: urls.BASE_RHPURL,
      headers: { isAnonymous: false },
    });
  }
  static postBlockGroup(clubId: string): Promise<IApiResponse> {
    const url = urls.POST_BLOCK_GROUP.replace('{ClubID}', clubId);
    return axios.post(url, null, {
      baseURL: urls.BASE_RHPURL,
      headers: { isAnonymous: false },
    });
  }

  static deleteUserClubEvent(clubId: string, eventId: number): Promise<IApiResponse> {
    let url = urls.GROUP_MANAGAMENT_EVENTS.replace('{ClubId}', clubId);
    const queryString = generateParams({
      ListID: [eventId],
    });
    if (queryString) {
      url = `${url}?${queryString}`;
    }
    return axios.delete(url, {
      baseURL: urls.BASE_RHPURL,
      headers: { isAnonymous: false },
    });
  }

  static postCreateUserClubMediaVideoURL(body: any): Promise<IApiResponse> {
    return axios.post(urls.POST_CREATE_USER_CLUB_MEDIA_VIDEO_URL, body, {
      baseURL: urls.BASE_RHPURL,
      headers: { isAnonymous: false },
    });
  }

  static getUserClubNewUsers(params: { pageSize?: number; page?: number; clubId?: string }) {
    const queryString = generateParams({
      Page: params?.page || 1,
      Size: params?.pageSize || PAGING_DEFAULT.PAGE_SIZE_MEMBER_SEARCH,
      // DemoMode: true,
    });
    let url = '';
    if (params.clubId) {
      url = `${urls.GET_USER_GROUP_NEW_USERS.replace('{ClubID}', params.clubId)}`;
    } else {
      url = `${urls.GET_USER_CLUB_NEW_USERS}`;
    }
    url += `${queryString ? '?' : ''}${queryString}`;

    // return this.restClient.execute(url, ApiMethod.GET, null).then(res => res);
    return axios.get(url, { baseURL: urls.BASE_RHPURL, headers: { isAnonymous: false } });
  }
}

export default groupApi;
